import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import AddMaterialsDialog from "./AddMaterialsDialog";
import EditeMaterialsDialog from "./EditeMaterialsDialog";
import { getMaterialsList } from "../../../../../services/Admin/MaterialsApi";
import { useAuth } from "../../../../../Context/AuthContext";

const getMaterialRules = (material) => {
  const rawRules = material?.rules;

  if (Array.isArray(rawRules)) {
    return rawRules.map((rule) => String(rule).trim()).filter(Boolean);
  }

  if (typeof rawRules === "string") {
    try {
      const parsedRules = JSON.parse(rawRules);
      if (Array.isArray(parsedRules)) {
        return parsedRules.map((rule) => String(rule).trim()).filter(Boolean);
      }
    } catch (error) {
      // fallback to line parsing below
    }

    return rawRules
      .split(/\r?\n/)
      .map((rule) => rule.trim())
      .filter(Boolean);
  }

  return [];
};

const MaterialsTab = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unitFilter, setUnitFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active");
  const { hasPermission, userRole, userType } = useAuth();

  const canViewDeleted = userType === "admin" && userRole === "admin";

  const unitOptions = useMemo(() => {
    const map = new Map();
    materials.forEach((material) => {
      const id =
        material?.measurment_unit?.id ||
        material?.measurement_unit?.id ||
        material?.measurment_unit_id ||
        material?.measurement_unit_id;
      const name = material?.measurment_unit?.name || material?.measurement_unit?.name;

      if (id && name && !map.has(String(id))) {
        map.set(String(id), name);
      }
    });

    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return materials.filter((material) => {
      const isDeleted = Number(material?.status) === 0;

      if (activeTab === "deleted") {
        if (!canViewDeleted || !isDeleted) return false;
      } else if (isDeleted) {
        return false;
      }

      const materialUnitId = String(
        material?.measurment_unit?.id ||
          material?.measurement_unit?.id ||
          material?.measurment_unit_id ||
          material?.measurement_unit_id ||
          ""
      );

      if (unitFilter !== "all" && materialUnitId !== unitFilter) {
        return false;
      }

      const statusValue = material?.status ? "active" : "inactive";
      if (statusFilter !== "all" && statusValue !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const keywordsValue = Array.isArray(material?.keywords)
        ? material.keywords.join(", ")
        : material?.keywords || "";
      const rulesValue = getMaterialRules(material).join(" ");

      const searchableText = [
        material?.id,
        material?.name,
        material?.measurment_unit?.name,
        material?.measurement_unit?.name,
        material?.cost_price_per_unit,
        material?.max_usage_per_product,
        keywordsValue,
        rulesValue,
        material?.status ? "متاح" : "غير متاح",
      ]
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [activeTab, canViewDeleted, materials, searchQuery, unitFilter, statusFilter]);

  useEffect(() => {
    if (!canViewDeleted && activeTab === "deleted") {
      setActiveTab("active");
    }
  }, [activeTab, canViewDeleted]);

  const fetchMaterials = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getMaterialsList();
      setMaterials(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchMaterials();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ color: "error.main", p: 2 }}>{String(error)}</Box>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          aria-label="materials tabs"
        >
          <Tab label="الخامات" value="active" />
          {canViewDeleted && <Tab label="المحذوفات" value="deleted" />}
        </Tabs>

        {activeTab === "active" && hasPermission("add") && (
          <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
            إضافة خامة
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 220px 180px" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          label="بحث في كل بيانات الخامات"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel id="materials-unit-filter-label">فلتر الوحدة</InputLabel>
          <Select
            labelId="materials-unit-filter-label"
            label="فلتر الوحدة"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <MenuItem value="all">كل الوحدات</MenuItem>
            {unitOptions.map((unit) => (
              <MenuItem key={unit.id} value={unit.id}>
                {unit.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="materials-status-filter-label">فلتر الحالة</InputLabel>
          <Select
            labelId="materials-status-filter-label"
            label="فلتر الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">كل الحالات</MenuItem>
            <MenuItem value="active">متاح</MenuItem>
            <MenuItem value="inactive">غير متاح</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>م</TableCell>
              <TableCell>الاسم</TableCell>
              <TableCell>الوحدة</TableCell>
              <TableCell>سعر الوحدة</TableCell>
              <TableCell>أقصى استخدام</TableCell>
              <TableCell>الكلمات المفتاحية</TableCell>
              <TableCell>القواعد</TableCell>
              <TableCell>الحالة</TableCell>
              {hasPermission("edit") && <TableCell align="right">إجراءات</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaterials.map((material, index) => (
              <TableRow
                key={material.id}
                hover
                sx={
                  material?.status
                    ? undefined
                    : {
                        backgroundColor: "#fff5f5",
                        "&:hover": {
                          backgroundColor: "#ffe8e8",
                        },
                      }
                }
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={material?.status ? undefined : { color: "error.main", fontWeight: 700 }}>
                  {material.name}
                </TableCell>
                <TableCell>{material.measurment_unit?.name || material.measurement_unit?.name || "-"}</TableCell>
                <TableCell>{material.cost_price_per_unit}</TableCell>
                <TableCell>{material.max_usage_per_product}</TableCell>
                <TableCell>
                  {Array.isArray(material.keywords)
                    ? material.keywords.join(", ")
                    : material.keywords || ""}
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  {getMaterialRules(material).length ? (
                    <Box component="ol" sx={{ m: 0, pl: 2.5, pr: 2.5 }}>
                      {getMaterialRules(material).map((rule, ruleIndex) => (
                        <Box component="li" key={`${material.id}-rule-${ruleIndex}`} sx={{ mb: 0.35 }}>
                          {rule}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={material.status ? "متاح" : "غير متاح"}
                    color={material.status ? "success" : "error"}
                    variant={material.status ? "filled" : "outlined"}
                  />
                </TableCell>
                {hasPermission("edit") && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="edit"
                      onClick={() => {
                        setEditMaterial(material);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!filteredMaterials.length && (
              <TableRow>
                <TableCell colSpan={hasPermission("edit") ? 9 : 8} align="center" sx={{ py: 3 }}>
                  لا توجد نتائج مطابقة للبحث أو الفلاتر
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddMaterialsDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={fetchMaterials}
      />
      <EditeMaterialsDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        material={editMaterial}
        onUpdated={() => {
          fetchMaterials();
          setEditDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default MaterialsTab;
